/**
 * Sub-component (not a routed page): master/detail container used by the admin pages'
 * tabs. Shows a PaginatedList of a given ListItemType, then swaps to a detail view for
 * the clicked item (TrainingDataCreateDisplay, BountyCreateEdit, or SubmissionDisplay
 * depending on the type). Preserves list scroll position, intercepts the browser back
 * button via history.pushState/popstate, and refreshes the list after deletes.
 * Data fetching happens inside PaginatedList; this component makes no API calls.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { ListItem, ListItemType } from "./AdminListItem";
import PaginatedList, { PaginatedListRef } from "../../components/PaginatedList";
import TrainingDataCreateDisplay from "@/pages/admin/ai/TrainingDataCreateDisplay";
import {
    SearchTrainingDataSetDisplay,
    SubmissionVerificationTrainingDataSetDisplay,
} from "@/types/trainingData";
import { RagDomainEntry } from "@/types/aiRag";
import { DataType } from "@/types/trainingData";
import { ChevronLeft } from "lucide-react";
import { AiBounty } from "@/types/aiBounty";
import BountyCreateEdit from "@/pages/bounty/BountyCreateEdit";
import { Bounty } from "@/types/bounty";
import SubmissionDisplay from "@/pages/submission/SubmissionDisplay";
import { SubmissionWithBounty } from "@/types/submission";

interface AdminListViewContainerProps {
    listItemType: ListItemType;
    dataType?: DataType;
}

const AdminListViewContainer: React.FC<AdminListViewContainerProps> = ({
    listItemType,
    dataType = "submission_verification",
}) => {
    const [selectedItem, setSelectedItem] = useState<ListItem | null>(null);
    const [showDomainModal, setShowDomainModal] = useState(false);
    const [newDomain, setNewDomain] = useState("");
    const paginatedListRef = useRef<PaginatedListRef>(null);
    const selectedItemRef = useRef<ListItem | null>(null);
    const listScrollRef = useRef<HTMLDivElement>(null);
    const detailScrollRef = useRef<HTMLDivElement>(null);
    const listScrollPositionRef = useRef<number>(0);

    // Keep ref in sync with state for back button
    useEffect(() => {
        selectedItemRef.current = selectedItem;
    }, [selectedItem]);

    const handleItemClick = (item: ListItem) => {
        // Save list scroll position for back button
        if (listScrollRef.current) {
            listScrollPositionRef.current = listScrollRef.current.scrollTop;
        }
        setSelectedItem(item);
    };

    const handleBackClick = useCallback(() => {
        setSelectedItem(null);
    }, []);

    const handleDomainSubmit = () => {
        if (!newDomain.trim()) return;
        // Create a pseudo entry with just the domain
        const pseudoEntry = { domain: newDomain.trim() } as RagDomainEntry;
        setSelectedItem(pseudoEntry);
        setShowDomainModal(false);
        setNewDomain("");
    };

    const handleDelete = () => {
        setSelectedItem(null);
        paginatedListRef.current?.refresh();
    };

    // Scroll detail to top when it opens
    useEffect(() => {
        if (selectedItem && detailScrollRef.current) {
            // Scroll to top, accounting for any top spacing
            requestAnimationFrame(() => {
                if (detailScrollRef.current) {
                    detailScrollRef.current.scrollTop = 0;
                }
            });
        }
    }, [selectedItem]);

    // Restore list scroll when going back
    useEffect(() => {
        if (!selectedItem && listScrollRef.current) {
            // Use requestAnimationFrame with a small delay to ensure DOM is ready
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (listScrollRef.current) {
                        listScrollRef.current.scrollTop = listScrollPositionRef.current;
                    }
                });
            });
        }
    }, [selectedItem]);

    // Intercept browser back button
    useEffect(() => {
        // Only push state on first selection to avoid history pollution
        // Multiple item selections should replace state, not push new entries
        if (selectedItem && !window.history.state?.adminView) {
            window.history.pushState({ adminView: true }, "");
        }
    }, [selectedItem]);

    useEffect(() => {
        const handlePopState = () => {
            // Use ref to get latest value without dependency issues
            if (selectedItemRef.current) {
                handleBackClick();
            }
        };

        // Listen for browser back/forward navigation
        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [handleBackClick]);

    return (
        <div className="relative w-full h-screen overflow-hidden">
            {/* List View - Hidden when detail is shown but stays mounted */}
            <div
                ref={listScrollRef}
                className={`absolute inset-0 overflow-y-auto ${selectedItem ? "hidden" : "block"}`}
            >
                <PaginatedList
                    ref={paginatedListRef}
                    itemType={listItemType}
                    onItemClick={handleItemClick}
                    dataType={dataType}
                />
            </div>

            {/* Domain Modal */}
            {showDomainModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96">
                        <h3 className="text-lg font-bold mb-4">New RAG Domain</h3>
                        <input
                            type="text"
                            value={newDomain}
                            onChange={(e) => setNewDomain(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleDomainSubmit()}
                            placeholder="e.g. grailed, stockx, ebay"
                            className="w-full px-3 py-2 border rounded-lg focus:border-black focus:outline-none mb-4"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowDomainModal(false)}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDomainSubmit}
                                disabled={!newDomain.trim()}
                                className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail View - Shows when item selected */}
            {selectedItem && (
                <div ref={detailScrollRef} className="absolute inset-0 overflow-y-auto bg-white">
                    {/* Back Button - positioned at top */}
                    <button
                        onClick={handleBackClick}
                        className="sticky top-2 left-2 p-2 rounded-full hover:bg-gray-200 z-20 float-left mb-2"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>

                    <div className="w-full h-full flex flex-col pt-6 clear-both relative">
                        {listItemType === "submission_verification_training_data_set" ? (
                            <TrainingDataCreateDisplay
                                mode="edit"
                                type="submission_verification"
                                trainingData={
                                    selectedItem as SubmissionVerificationTrainingDataSetDisplay
                                }
                                onDelete={handleDelete}
                            />
                        ) : listItemType === "bounty" ? (
                            <BountyCreateEdit
                                type="normal"
                                mode="edit"
                                bounty={selectedItem as Bounty}
                                onDelete={handleDelete}
                            />
                        ) : listItemType === "ai_bounty" ? (
                            <BountyCreateEdit
                                type="ai"
                                mode="edit"
                                bounty={selectedItem as AiBounty}
                                onDelete={handleDelete}
                            />
                        ) : listItemType === "submission" ? (
                            <SubmissionDisplay
                                type="owner"
                                submissionType="normal"
                                submission={selectedItem as SubmissionWithBounty}
                                onDelete={handleDelete}
                            />
                        ) : listItemType === "ai_submission" ? (
                            <SubmissionDisplay
                                type="owner"
                                submissionType="ai"
                                submission={selectedItem as SubmissionWithBounty}
                                onDelete={handleDelete}
                            />
                        ) : listItemType === "search_training_data_set" ? (
                            <TrainingDataCreateDisplay
                                mode="edit"
                                type="search"
                                trainingData={selectedItem as SearchTrainingDataSetDisplay}
                                onDelete={handleDelete}
                            />
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminListViewContainer;
